// src/plugins/maintenanceGuard.js
import { MaintenanceService } from '@/views/panelMantenimiento/Services/MaintenanceService';

const ALLOW = [
  /^\/login$/,
  /^\/panel-mantenimiento/,
  /^\/mantenimiento$/,
  /^\/registrarse$/,
  /^\/solicitar-recuperacion$/,
  /^\/expired-recovery$/,
  /^\/cambiar_pass$/,
  /^\/magic-link-login(\/[\w-]+)?$/,
  /^\/registro-control(\/[\w-]+)?$/
];

const UPCOMING_TOAST_KEY = 'maint_upcoming_warned_v1';

function isAllowed(path) {
  return ALLOW.some(rx => rx.test(path));
}
function parseMs(iso) {
  const t = Date.parse(iso);
  return Number.isNaN(t) ? null : t;
}
function safeRedirect(q) {
  const r = typeof q?.redirect === 'string' ? q.redirect : '';
  if (!r.startsWith('/')) return '/';
  if (r === '/mantenimiento') return '/';
  return r;
}

let watcherStarted = false;

export function installMaintenanceGuard(router, { pollMs = 120000 } = {}) {
  // ✅ Guard “clásico”: bloquea al navegar
  router.beforeEach(async (to) => {
    try {
      const status = await MaintenanceService.getStatus(); // cache ok aquí
      if (status?.enabled && status?.active) {
        const ok = isAllowed(to.path);
        if (!ok) {
          return { path: '/mantenimiento', replace: true, query: { redirect: to.fullPath } };
        }
      } else if (status?.enabled && status?.upcoming) {
        if (!sessionStorage.getItem(UPCOMING_TOAST_KEY)) {
          sessionStorage.setItem(UPCOMING_TOAST_KEY, '1');
          const eta = status.startsAt ? new Date(status.startsAt).toLocaleString('es-MX') : '';
          window?.$toast?.warning?.(`Habrá mantenimiento programado ${eta ? `el ${eta}` : ''}`.trim());
        }
      }
    } catch {
      /* noop */
    }
  });

  // ✅ Watcher global: autobloquea / autolibera aunque NO naveguen
  if (!watcherStarted) {
    watcherStarted = true;
    startMaintenanceWatcher(router, { pollMs });
  }
}
function startMaintenanceWatcher(router, { pollMs = 120000 } = {}) {
  let intervalId = null;
  let boundaryTimeoutId = null;
  let checking = false;

  let last = { enabled: false, active: false, upcoming: false };

  function clearTimers() {
    if (intervalId) clearInterval(intervalId);
    intervalId = null;
    if (boundaryTimeoutId) clearTimeout(boundaryTimeoutId);
    boundaryTimeoutId = null;
  }

  function scheduleBoundary(status) {
    if (boundaryTimeoutId) clearTimeout(boundaryTimeoutId);
    boundaryTimeoutId = null;

    if (!status?.enabled) return;

    const now = Date.now();
    const s = parseMs(status.startsAt);
    const e = parseMs(status.endsAt);

    let target = null;
    if (status.upcoming && s != null) target = s;
    if (status.active && e != null) target = e;

    if (target == null) return;

    const ms = Math.max(0, target - now + 1500);
    boundaryTimeoutId = setTimeout(() => check({ force: true }), ms);
  }

  async function enforce(status) {
    const current = router.currentRoute.value;

    // Si se volvió ACTIVE, manda a /mantenimiento aunque el usuario no navegue
    if (status?.enabled && status?.active) {
      if (!isAllowed(current.path)) {
        await router.replace({
          path: '/mantenimiento',
          query: { redirect: current.fullPath }
        });
      }
      return;
    }

    // Si ya terminó y estás en /mantenimiento -> regresa + reload
    if (last.active && !(status?.enabled && status?.active)) {
      if (current.path === '/mantenimiento') {
        const redirect = safeRedirect(current.query);
        await router.replace(redirect);
        window.location.reload();
      }
    }
  }

  async function check({ force = false } = {}) {
    if (checking) return;
    checking = true;
    try {
      const status = await MaintenanceService.getStatus({ force });
      await enforce(status);

      if (status?.enabled && status?.upcoming && !sessionStorage.getItem(UPCOMING_TOAST_KEY)) {
        sessionStorage.setItem(UPCOMING_TOAST_KEY, '1');
        const eta = status.startsAt ? new Date(status.startsAt).toLocaleString('es-MX') : '';
        window?.$toast?.warning?.(`Habrá mantenimiento programado ${eta ? `el ${eta}` : ''}`.trim());
      }

      scheduleBoundary(status);
      last = status || last;
    } catch {
      // noop
    } finally {
      checking = false;
    }
  }

  // Handlers con referencia (para poder removerlos)
  const onVisibility = () => { if (!document.hidden) check({ force: true }); };
  const onOnline = () => check({ force: true });
  const onBeforeUnload = () => {
    clearTimers();
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('online', onOnline);
    window.removeEventListener('beforeunload', onBeforeUnload);
  };

  // ✅ Poll cada 2 min
  intervalId = setInterval(() => check({ force: true }), pollMs);

  // ✅ Check inicial
  check({ force: true });

  // ✅ Re-checa al volver a la pestaña / reconectar
  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('online', onOnline);

  // ✅ Limpieza (y así clearTimers “se usa”)
  window.addEventListener('beforeunload', onBeforeUnload);
}